import React, { useState, useEffect, useCallback } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Popconfirm, Tabs, Card, InputNumber, Switch, Alert, Button, Input, message, Select, Modal, Typography, Table, Form, Space, Tag, Tooltip } from 'antd';
import { DeleteOutlined, InfoCircleOutlined, CopyOutlined, PlusOutlined, UserOutlined, NodeIndexOutlined, FormOutlined, DatabaseOutlined, SettingOutlined } from '@ant-design/icons';
import { Icon } from '@iconify/react';
import FormSelector from '../common/FormSelector';
import GenericNodeContent from '../common/GenericNodeContent';
import { adminInstance } from '../api/axiosConfig';
import config from '../../config';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const selectStyle = {
  width: '100%',
  marginBottom: '8px',
};

const TriggerNode = ({ id, data, isConnectable }) => {
  const [gravityForms, setGravityForms] = useState([]);
  const [wpForms, setWpForms] = useState([]);
  const [cf7Forms, setCf7Forms] = useState([]);
  const [ninjaForms, setNinjaForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [selectedFields, setSelectedFields] = useState(data.selectedFields || []);
  const [nodeName, setNodeName] = useState(data.nodeName || `Trigger ${id}`);
  const [webhookKeys, setWebhookKeys] = useState(data.webhookKeys || []);
  const [isSamplingWebhook, setIsSamplingWebhook] = useState(false);
  const [manualKeyInput, setManualKeyInput] = useState('');
  const [manualValueType, setManualValueType] = useState('string');
  const [wpCoreTriggers, setWpCoreTriggers] = useState([]);
  const [selectedWpCoreTrigger, setSelectedWpCoreTrigger] = useState('');
  const [wpCoreTriggerConditions, setWpCoreTriggerConditions] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [postTypes, setPostTypes] = useState([]);


  const fetchGravityForms = useCallback(async () => {

    if (data.isViewer) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/gravity-forms`);
      setGravityForms(response.data);
    } catch (error) {
      console.error('Error fetching Gravity Forms:', error);
      setError('Failed to fetch Gravity Forms. Please try again.');
      message.error('Failed to fetch Gravity Forms');
    }
    setLoading(false);
  }, []);

  const fetchWpForms = useCallback(async () => {

    if (data.isViewer) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
        const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/wpforms`);
        setWpForms(response.data);
    } catch (error) {
        console.error('Error fetching WPForms:', error);
        setError('Failed to fetch WPForms. Please try again.');
        message.error('Failed to fetch WPForms');
    }
    setLoading(false);
  }, []);

  const fetchCf7Forms = useCallback(async () => {

    if (data.isViewer) {
      return;
    }


    setLoading(true);
    setError(null);
    try {
        const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/contactform7`);
        setCf7Forms(response.data);
    } catch (error) {
        console.error('Error fetching Contact Form 7:', error);
        setError('Failed to fetch Contact Form 7. Please try again.');
        message.error('Failed to fetch Contact Form 7');
    }
    setLoading(false);
  }, []);

  const fetchNinjaForms = useCallback(async () => {

    if (data.isViewer) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
        const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/ninjaforms`);
        setNinjaForms(response.data);
    } catch (error) {
        console.error('Error fetching Ninja Forms:', error);
        setError('Failed to fetch Ninja Forms. Please try again.');
        message.error('Failed to fetch Ninja Forms');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (data.triggerType === 'gravityForms') {
      fetchGravityForms();
    } else if (data.triggerType === 'wpForms') {
      fetchWpForms();
    } else if (data.triggerType === 'contactForm7') {
      fetchCf7Forms();
    } else if (data.triggerType === 'ninjaForms') {
      fetchNinjaForms();
    } else if (data.triggerType === 'webhook') {
      if (!data.webhookUrl) {
        generateWebhookUrl();
      } else {
        setWebhookUrl(data.webhookUrl);
      }
    }
  }, [data.triggerType, data.webhookUrl, fetchGravityForms, fetchWpForms, fetchCf7Forms, fetchNinjaForms]);


  const fetchPostTypes = useCallback(async () => {

    if (data.isViewer) {
      return;
    }

    try {
      setLoading(true);
      const response = await adminInstance.get('/post-types');
      setPostTypes(response.data);
    } catch (error) {
      console.error('Error fetching post types:', error);
      message.error('Failed to fetch post types');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleWorkflowChange = (workflowId) => {
    data.updateNodeData(id, { selectedWorkflow: workflowId });
  };

  const handleGravityFormsChange = (formId, fields) => {
    data.updateNodeData(id, { 
      selectedForm: formId,
      selectedFields: fields,
    });
    setSelectedFields(fields);
  };

  const handleContentChange = (nodeId, newData) => {
    data.updateNodeData(nodeId, newData);
  };

  const handleFormChange = (formId, fields) => {
    console.log('Selected Form fields:', fields);
    data.updateNodeData(id, { 
        selectedForm: formId,
        selectedFields: fields,
    });
    setSelectedFields(fields);
};

  const renderTriggerIcon = (type) => {
    switch (type) {
      case 'manual':
        return <UserOutlined style={{ color: '#1890ff' }} />;
      case 'gravityForms':
        return <FormOutlined style={{ color: '#52c41a' }} />;
      case 'wpForms':
        return <FormOutlined style={{ color: '#52c41a' }} />;
      case 'contactForm7':
        return <FormOutlined style={{ color: '#52c41a' }} />;
      case 'ninjaForms':
        return <FormOutlined style={{ color: '#52c41a' }} />;
      case 'webhook':
        return <Icon icon="material-symbols:webhook" style={{ color: '#722ed1' }} />;
      case 'workflowOutput':
        return <NodeIndexOutlined style={{ color: '#fa8c16' }} />;
      case 'wpCore':
        return <Icon icon="ic:outline-wordpress" style={{ color: '#1890ff' }} />;
      case 'rss':
        return <Icon icon="simple-icons:rss" style={{ color: '#f5222d' }} />;
      default:
        return null;
    }
  };

  const getTriggerTypeLabel = () => {
    if (!data.triggerType) return 'Select trigger type';
    const labels = {
      manual: 'Manual',
      gravityForms: 'Gravity Forms',
      wpForms: 'WPForms',
      contactForm7: 'Contact Form 7',
      ninjaForms: 'Ninja Forms',
      webhook: 'Webhook',
      workflowOutput: 'Workflow Output',
      wpCore: 'WordPress',
      rss: 'RSS Feed'
    };
    return labels[data.triggerType];
  };

  const fetchWorkflows = useCallback(async () => {

    if (data.isViewer) {
      return;
    }

    try {
      const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/workflows`);
      setWorkflows(response.data.filter(wf => wf.id !== data.workflowId)); // Exclude current workflow
    } catch (error) {
      console.error('Error fetching workflows:', error);
      message.error('Failed to fetch workflows');
    }
  }, [data.workflowId]);


  useEffect(() => {
    if (data.triggerType === 'workflowOutput') {
      fetchWorkflows();
    }
  }, [data.triggerType, fetchWorkflows]);

  const generateWebhookUrl = async () => {
    try {
      const response = await adminInstance.post(`${config.apiSettings.root}wp-ai-workflows/v1/generate-webhook`, { nodeId: id });
      const newWebhookUrl = response.data.webhookUrl;
      setWebhookUrl(newWebhookUrl);
      data.updateNodeData(id, { webhookUrl: newWebhookUrl });
    } catch (error) {
      console.error('Error generating webhook URL:', error);
      message.error('Failed to generate webhook URL');
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    message.success('Webhook URL copied to clipboard');
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const webhookInstructions = (
    <div style={{ padding: '10px' }}>
      <Title level={4}>Understanding and Using Webhook Triggers</Title>
      
      <Paragraph>
        <strong>What are Webhooks?</strong><br />
        Webhooks let you receive data from external applications or forms in real-time. When something happens (like a form submission), the data is sent to your webhook URL automatically.
      </Paragraph>
  
      <Title level={5}>Quick Setup Steps</Title>
      <ol>
        <li>Copy your webhook URL above</li>
        <li>Use it as the submission endpoint in your forms or applications</li>
        <li>Test it using the "Get Samples" button to see the data structure</li>
      </ol>
  
      <Title level={5}>Implementation Options</Title>
      
      <Tabs defaultActiveKey="1" style={{ marginBottom: '16px' }}>
        <Tabs.TabPane tab="Basic Webhook" key="1">
          <Card title="Simple Data Collection" style={{ marginBottom: '16px' }}>
            <Paragraph>
              For just collecting data without displaying results:
            </Paragraph>
            <div style={{ position: 'relative' }}>
              <pre style={{ background: '#f6f8fa', padding: '12px', borderRadius: '4px', overflow: 'auto' }}>
                {`<form action="${webhookUrl}" method="post">
    <input type="text" name="user_name" placeholder="Name">
    <input type="email" name="user_email" placeholder="Email">
    <button type="submit">Submit</button>
</form>`}
              </pre>
              <Button 
                icon={<CopyOutlined />} 
                style={{ position: 'absolute', top: '8px', right: '8px' }}
                onClick={() => {
                    navigator.clipboard.writeText(document.querySelector('pre').textContent);
                    message.success('Code copied!');
                }}
              />
            </div>
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab="With Live Results" key="2">
          <Card title="Setup Steps" style={{ marginBottom: '16px' }}>
            <ol>
              <li>Add an "Output" node to your workflow
                <Text type="secondary" style={{ display: 'block', marginLeft: '24px' }}>
                  This will generate a shortcode that looks like: [wp_ai_workflows_output id="workflow-id"]
                </Text>
              </li>
              <li>Note the workflow ID from your shortcode
                <Text type="secondary" style={{ display: 'block', marginLeft: '24px' }}>
                  Replace 'YOUR-WORKFLOW-ID' in the code examples with this ID
                </Text>
              </li>
              <li>Choose an implementation method:</li>
            </ol>

            <Tabs size="small">
              <Tabs.TabPane tab="HTML Form" key="html">
                <div style={{ position: 'relative' }}>
                  <pre style={{ background: '#f6f8fa', padding: '12px', borderRadius: '4px', overflow: 'auto' }}>
                    {`<!-- Add wp-ai-workflows-trigger class -->
<form action="${webhookUrl}" 
      method="post" 
      class="wp-ai-workflows-trigger">
    <input type="text" name="user_name">
    <button type="submit">Submit</button>
</form>`}
                  </pre>
                  <Button 
                    icon={<CopyOutlined />} 
                    style={{ position: 'absolute', top: '8px', right: '8px' }}
                    onClick={() => {
                        navigator.clipboard.writeText(document.querySelector('pre').textContent);
                        message.success('Code copied!');
                    }}
                  />
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane tab="AJAX" key="ajax">
                <Alert
                  message="Important"
                  description="Remember to replace 'YOUR-WORKFLOW-ID' with the ID from your output node's shortcode"
                  type="info"
                  showIcon
                  style={{ marginBottom: '12px' }}
                />
                <div style={{ position: 'relative' }}>
                  <pre style={{ background: '#f6f8fa', padding: '12px', borderRadius: '4px', overflow: 'auto' }}>
                    {`<form id="ajaxForm" onsubmit="submitForm(event)">
    <input type="text" name="user_name">
    <button type="submit">Submit</button>
</form>

<script>
async function submitForm(event) {
    event.preventDefault();
    
    // Replace YOUR-WORKFLOW-ID with the ID from your shortcode
    window.wpAiWorkflows['YOUR-WORKFLOW-ID']
        .triggerSubmission();

    // Submit data to webhook
    const response = await fetch('${webhookUrl}', {
        method: 'POST',
        body: new FormData(event.target)
    });

    if (!response.ok) {
        console.error('Submission failed');
    }
}
</script>`}
                  </pre>
                  <Button 
                    icon={<CopyOutlined />} 
                    style={{ position: 'absolute', top: '8px', right: '8px' }}
                    onClick={() => {
                        navigator.clipboard.writeText(document.querySelector('pre').textContent);
                        message.success('Code copied!');
                    }}
                  />
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane tab="JavaScript Framework" key="framework">
  <Alert
    message="Important"
    description="Remember to replace 'YOUR-WORKFLOW-ID' with the ID from your output node's shortcode"
    type="info"
    showIcon
    style={{ marginBottom: '12px' }}
  />
  <div style={{ position: 'relative' }}>
    <pre style={{ background: '#f6f8fa', padding: '12px', borderRadius: '4px', overflow: 'auto' }}>
      {`const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Trigger the shortcode loading state
    window.wpAiWorkflows['YOUR-WORKFLOW-ID']
        .triggerSubmission();

    try {
        const response = await fetch('${webhookUrl}', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                // your data here
            })
        });

        if (!response.ok) {
            throw new Error('Submission failed');
        }
    } catch (error) {
        console.error('Error:', error);
    }
};`}
    </pre>
    <Button 
      icon={<CopyOutlined />} 
      style={{ position: 'absolute', top: '8px', right: '8px' }}
      onClick={() => {
        navigator.clipboard.writeText(document.querySelector('pre').textContent);
        message.success('Code copied!');
      }}
    />
  </div>
</Tabs.TabPane>
            </Tabs>
          </Card>

          <Alert
            message="How it works"
            description={
              <ul style={{ marginBottom: 0 }}>
                <li>The output node creates a shortcode for displaying workflow results</li>
                <li>Place this shortcode where you want the results to appear on your page</li>
                <li>When a form is submitted, the container shows a loading state</li>
                <li>Results appear automatically once the workflow completes</li>
              </ul>
            }
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        </Tabs.TabPane>
      </Tabs>

      <Title level={5}>Accessing Webhook Data</Title>
      <Paragraph>
        In your workflow, access submitted data using these tags:
        <ul>
          <li><Text code>[[field_name] from trigger-node-id]</Text> - Access specific fields</li>
          <li><Text code>[Input from trigger-node-id]</Text> - Access entire payload</li>
        </ul>
      </Paragraph>

      <Alert
        message="Security Note"
        description="Ensure your webhook URL remains private and validate incoming data in your workflow nodes."
        type="warning"
        showIcon
        style={{ marginTop: '16px' }}
      />
    </div>
  );

  const getSamples = async () => {
    setIsSamplingWebhook(true);
    try {
      const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/sample-webhook/${id}`);
      if (response.data.keys) {
        setWebhookKeys(response.data.keys);
        data.updateNodeData(id, { webhookKeys: response.data.keys });
      } else {
        message.warning('No webhook data received within the timeout period');
      }
    } catch (error) {
      console.error('Error sampling webhook:', error);
      message.error('Failed to sample webhook');
    }
    setIsSamplingWebhook(false);
  };

  const addManualKey = () => {
    if (manualKeyInput) {
      const newKey = { key: manualKeyInput, type: manualValueType };
      const updatedKeys = [...webhookKeys, newKey];
      setWebhookKeys(updatedKeys);
      data.updateNodeData(id, { webhookKeys: updatedKeys });
      setManualKeyInput('');
      setManualValueType('string');
    }
  };

  const removeKey = (keyToRemove) => {
    const updatedKeys = webhookKeys.filter(k => k.key !== keyToRemove);
    setWebhookKeys(updatedKeys);
    data.updateNodeData(id, { webhookKeys: updatedKeys });
  };

  const columns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button onClick={() => removeKey(record.key)} type="link" danger>
          Remove
        </Button>
      ),
    },
  ];


  const handleNameChange = (e) => {
    const newName = e.target.value;
    setNodeName(newName);
    data.updateNodeData(id, { nodeName: newName });
  };

  const fetchWpCoreTriggers = useCallback(async () => {

    if (data.isViewer) {
      return;
    }
    
    try {
      const response = await adminInstance.get(`${config.apiSettings.root}wp-ai-workflows/v1/wp-core-triggers`);
      setWpCoreTriggers(response.data);
    } catch (error) {
      console.error('Error fetching WordPress core triggers:', error);
      message.error('Failed to fetch WordPress core triggers');
    }
  }, []);

  useEffect(() => {
    if (data.triggerType === 'wpCore') {
      fetchWpCoreTriggers();
      setSelectedWpCoreTrigger(data.selectedWpCoreTrigger || '');
      setWpCoreTriggerConditions(data.wpCoreTriggerConditions || {});
      fetchPostTypes(); 
    }
  }, [data.triggerType, fetchWpCoreTriggers, data.selectedWpCoreTrigger, data.wpCoreTriggerConditions, fetchPostTypes]);

  const handleTriggerTypeChange = (value) => {
    data.updateNodeData(id, { 
      triggerType: value, 
      selectedForm: null, 
      selectedFields: [],
      selectedWorkflow: null,
      selectedWpCoreTrigger: null,
      wpCoreTriggerConditions: {},
      selectedTable: null,
      databaseTriggerType: null
    });
  };

  const handleWpCoreTriggerChange = (value) => {
    setSelectedWpCoreTrigger(value);
    data.updateNodeData(id, { 
      selectedWpCoreTrigger: value,
      wpCoreTriggerConditions: {}
    });
  };

  const handleWpCoreTriggerConditionChange = (conditions) => {
    setWpCoreTriggerConditions(conditions);
    data.updateNodeData(id, { wpCoreTriggerConditions: conditions });
  };

  const renderPostTypeSelect = () => (
    <Form.Item label="Post Type">
      <Select
        className="nodrag"
        value={wpCoreTriggerConditions.postType}
        onChange={(value) => handleWpCoreTriggerConditionChange({ ...wpCoreTriggerConditions, postType: value })}
        loading={loading}
      >
        <Option value="any">Any</Option>
        {postTypes.map(type => (
          <Option key={type.name} value={type.name}>
            {type.label}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );

  const renderWpCoreTriggerConditions = () => {
    switch (selectedWpCoreTrigger) {
      case 'publish_post':
        return renderPostTypeSelect();
      case 'user_register':
        return (
          <Form.Item label="User Role">
            <Select
            className="nodrag"
              value={wpCoreTriggerConditions.userRole}
              onChange={(value) => handleWpCoreTriggerConditionChange({ ...wpCoreTriggerConditions, userRole: value })}
            >
              <Option value="any">Any</Option>
              <Option value="subscriber">Subscriber</Option>
              <Option value="contributor">Contributor</Option>
              <Option value="author">Author</Option>
              <Option value="editor">Editor</Option>
              <Option value="administrator">Administrator</Option>
            </Select>
          </Form.Item>
        );
      case 'wp_insert_comment':
        return (
          <>
            <Form.Item label="Comment Type">
              <Select
                className="nodrag"
                value={wpCoreTriggerConditions.commentType}
                onChange={(value) => handleWpCoreTriggerConditionChange({ ...wpCoreTriggerConditions, commentType: value })}
              >
                <Option value="any">Any</Option>
                <Option value="comment">Comment</Option>
                <Option value="pingback">Pingback</Option>
                <Option value="trackback">Trackback</Option>
              </Select>
            </Form.Item>
            {renderPostTypeSelect()}
          </>
        );
      case 'wp_login':
        return (
          <Form.Item label="User Role">
            <Select
            className="nodrag"
              value={wpCoreTriggerConditions.userRole}
              onChange={(value) => handleWpCoreTriggerConditionChange({ ...wpCoreTriggerConditions, userRole: value })}
            >
              <Option value="any">Any</Option>
              <Option value="subscriber">Subscriber</Option>
              <Option value="contributor">Contributor</Option>
              <Option value="author">Author</Option>
              <Option value="editor">Editor</Option>
              <Option value="administrator">Administrator</Option>
            </Select>
          </Form.Item>
        );
      case 'transition_post_status':
        return (
          <>
            <Form.Item label="From Status">
              <Select
                className="nodrag"
                value={wpCoreTriggerConditions.fromStatus}
                onChange={(value) => handleWpCoreTriggerConditionChange({ ...wpCoreTriggerConditions, fromStatus: value })}
              >
                <Option value="any">Any</Option>
                <Option value="publish">Published</Option>
                <Option value="draft">Draft</Option>
                <Option value="pending">Pending</Option>
                <Option value="private">Private</Option>
                <Option value="trash">Trash</Option>
              </Select>
            </Form.Item>
            <Form.Item label="To Status">
              <Select
                className="nodrag"
                value={wpCoreTriggerConditions.toStatus}
                onChange={(value) => handleWpCoreTriggerConditionChange({ ...wpCoreTriggerConditions, toStatus: value })}
              >
                <Option value="any">Any</Option>
                <Option value="publish">Published</Option>
                <Option value="draft">Draft</Option>
                <Option value="pending">Pending</Option>
                <Option value="private">Private</Option>
                <Option value="trash">Trash</Option>
              </Select>
            </Form.Item>
            {renderPostTypeSelect()}
          </>
        );
      default:
        return null;
    }
  };

  const [rssSettings, setRssSettings] = useState({
    feedUrl: data.rssSettings?.feedUrl || '',
    pollingInterval: data.rssSettings?.pollingInterval || '15min',
    filters: {
      title: data.rssSettings?.filters?.title || '',
      content: data.rssSettings?.filters?.content || '',
      categories: data.rssSettings?.filters?.categories || []
    },
    maxItems: data.rssSettings?.maxItems || 10,
    includeContent: data.rssSettings?.includeContent || false
  });

  const fetchPreview = async () => {
    if (!rssSettings.feedUrl) {
      message.error('Please enter a feed URL first');
      return;
    }
  
    setPreviewLoading(true);
    try {
      const response = await adminInstance.post('/rss-preview', {
        feedUrl: rssSettings.feedUrl
      });
      
      setPreviewData(response.data.items);
    } catch (error) {
      message.error('Failed to fetch RSS feed preview');
    } finally {
      setPreviewLoading(false);
    }
  };



  return (
    <Card 
      title={
        <Space>
          {renderTriggerIcon(data.triggerType)}
          <span>Trigger</span>
          {data.triggerType && (
            <Tag color="blue">{getTriggerTypeLabel()}</Tag>
          )}
        </Space>
      }
      style={{ width: 380 }}
      extra={<Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>}
      className="trigger-node"
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={handleNameChange}
        style={{ marginBottom: 16 }}
        prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
        className="nodrag"
      />
  
      <Select
        style={{ width: '100%', marginBottom: 16 }}
        className="nodrag trigger-type-select"
        value={data.triggerType || ''}
        onChange={handleTriggerTypeChange}
        placeholder="Select trigger type"
      >
        {[
          { value: 'manual', icon: <UserOutlined />, label: 'Manual Trigger' },
          { value: 'gravityForms', icon: <FormOutlined />, label: 'Gravity Forms Trigger' },
          { value: 'wpForms', icon: <FormOutlined />, label: 'WPForms Trigger' },
          { value: 'contactForm7', icon: <FormOutlined />, label: 'Contact Form 7 Trigger' },
          { value: 'ninjaForms', icon: <FormOutlined />, label: 'Ninja Forms Trigger' },
          { value: 'webhook', icon: <Icon icon="material-symbols:webhook" />, label: 'Webhook Trigger' },
          { value: 'workflowOutput', icon: <NodeIndexOutlined />, label: 'Workflow Output Trigger' },
          { value: 'wpCore', icon: <Icon icon="ic:outline-wordpress" />, label: 'WordPress Core Trigger' },
          { value: 'rss', icon: <Icon icon="simple-icons:rss" />, label: 'RSS Feed Trigger' }
        ].map(option => (
          <Option key={option.value} value={option.value}>
            <Space>
              {option.icon}
              <Text>{option.label}</Text>
            </Space>
          </Option>
        ))}
      </Select>
  
      {(data.triggerType === 'gravityForms' || 
        data.triggerType === 'wpForms' || 
        data.triggerType === 'contactForm7' ||
        data.triggerType === 'ninjaForms') && (
          <div style={{ marginBottom: 16 }}>
              <FormSelector
                  forms={
                      data.triggerType === 'gravityForms' ? gravityForms : 
                      data.triggerType === 'wpForms' ? wpForms :
                      data.triggerType === 'ninjaForms' ? ninjaForms :
                      cf7Forms
                  }
                  loading={loading}
                  error={error}
                  selectedForm={data.selectedForm}
                  selectedFields={selectedFields}
                  onChange={handleFormChange}
                  onRetry={
                      data.triggerType === 'gravityForms' ? fetchGravityForms : 
                      data.triggerType === 'wpForms' ? fetchWpForms :
                      data.triggerType === 'ninjaForms' ? fetchNinjaForms :
                      fetchCf7Forms
                  }
                  formType={data.triggerType}
              />
          </div>
      )}
      
      {data.triggerType === 'manual' && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ marginBottom: 8, display: 'block' }}>Trigger Content</Text>
          <GenericNodeContent
            id={id}
            data={data}
            onChange={handleContentChange}
            placeholder="Enter trigger content..."
          />
        </div>
      )}
  
      {data.triggerType === 'webhook' && (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <div>
            <Text strong>Webhook URL</Text>
            <Input.Group compact style={{ marginTop: 8 }}>
              <Input
                style={{ width: 'calc(100% - 72px)' }}
                value={webhookUrl}
                placeholder="Webhook URL will be generated"
                readOnly
              />
              <Tooltip title="Copy URL">
                <Button icon={<CopyOutlined />} onClick={copyWebhookUrl} />
              </Tooltip>
              <Tooltip title="View Instructions">
                <Button icon={<InfoCircleOutlined />} onClick={showModal} />
              </Tooltip>
            </Input.Group>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Click the info icon for usage instructions
            </Text>
          </div>
  
          <Button 
            onClick={getSamples} 
            loading={isSamplingWebhook}
            icon={<Icon icon="material-symbols:stacks-outline" />}
          >
            Get Samples
          </Button>
  
          <div style={{ marginTop: 8 }}>
            <Text strong>Webhook Keys</Text>
            <Table 
              dataSource={webhookKeys} 
              columns={columns} 
              pagination={false}
              size="small"
              style={{ marginTop: 8 }}
            />
          </div>
  
          <div style={{ marginTop: 8 }}>
            <Text strong>Add Manual Key</Text>
            <Space.Compact style={{ width: '100%', marginTop: 8 }}>
              <Input
                className="nodrag"
                placeholder="Enter key"
                value={manualKeyInput}
                onChange={(e) => setManualKeyInput(e.target.value)}
              />
              <Select
                className="nodrag"
                value={manualValueType}
                onChange={setManualValueType}
                style={{ width: 120 }}
              >
                <Option value="string">String</Option>
                <Option value="number">Number</Option>
                <Option value="boolean">Boolean</Option>
              </Select>
              <Button icon={<PlusOutlined />} onClick={addManualKey}>Add</Button>
            </Space.Compact>
          </div>
        </Space>
      )}
  
      {data.triggerType === 'workflowOutput' && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>Select Workflow</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            value={data.selectedWorkflow}
            onChange={handleWorkflowChange}
            placeholder="Select a workflow"
            className="nodrag"
          >
            {workflows.map(workflow => (
              <Option key={workflow.id} value={workflow.id}>{workflow.name}</Option>
            ))}
          </Select>
        </div>
      )}
  
      {data.triggerType === 'wpCore' && (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <div>
            <Text strong>WordPress Trigger</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              className="nodrag"
              value={selectedWpCoreTrigger}
              onChange={handleWpCoreTriggerChange}
              placeholder="Select WordPress Core Trigger"
            >
              {wpCoreTriggers.map(trigger => (
                <Option key={trigger.value} value={trigger.value}>{trigger.label}</Option>
              ))}
            </Select>
          </div>
  
          {selectedWpCoreTrigger && (
            <div style={{ marginTop: 8 }}>
              <Text strong>Trigger Conditions</Text>
              <div style={{ marginTop: 8 }}>
                {renderWpCoreTriggerConditions()}
              </div>
            </div>
          )}
        </Space>
      )}
  
      {data.triggerType === 'rss' && (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <div>
            <Text strong>RSS Feed URL</Text>
            <Input
              placeholder="Enter RSS feed URL..." 
              value={rssSettings.feedUrl}
              onChange={(e) => {
                const newSettings = { ...rssSettings, feedUrl: e.target.value };
                setRssSettings(newSettings);
                data.updateNodeData(id, { rssSettings: newSettings });
              }}
              className="nodrag"
              style={{ marginTop: 8 }}
              suffix={
                <Button 
                  type="link" 
                  size="small" 
                  onClick={fetchPreview}
                  loading={previewLoading}
                >
                  Preview
                </Button>
              }
            />
          </div>
  
          {previewData && (
            <div>
              <Text strong>Preview</Text>
              <Table
                size="small"
                dataSource={previewData}
                columns={[
                  {
                    title: 'Title',
                    dataIndex: 'title',
                    key: 'title',
                    ellipsis: true
                  },
                  {
                    title: 'Categories',
                    dataIndex: 'categories',
                    key: 'categories',
                    render: (cats) => cats?.join(', '),
                    ellipsis: true
                  }
                ]}
                pagination={false}
                style={{ marginTop: 8 }}
                scroll={{ y: 200 }}
              />
            </div>
          )}
  
          <div>
            <Text strong>Update Interval</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={rssSettings.pollingInterval}
              onChange={(value) => {
                const newSettings = { ...rssSettings, pollingInterval: value };
                setRssSettings(newSettings);
                data.updateNodeData(id, { rssSettings: newSettings });
              }}
              className="nodrag"
            >
              {[
                { value: '5min', label: '5 minutes' },
                { value: '15min', label: '15 minutes' },
                { value: '30min', label: '30 minutes' },
                { value: '1hour', label: '1 hour' },
                { value: '6hours', label: '6 hours' },
                { value: '24hours', label: '24 hours' },
                { value: 'Custom', label: 'Use Schedule to Trigger' }
              ].map(option => (
                <Option key={option.value} value={option.value}>{option.label}</Option>
              ))}
            </Select>
          </div>
  
          <div>
            <Text strong>Filters</Text>
            <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
              <Input
                placeholder="Title contains..."
                value={rssSettings.filters.title}
                onChange={(e) => {
                  const newSettings = {
                    ...rssSettings,
                    filters: { ...rssSettings.filters, title: e.target.value }
                  };
                  setRssSettings(newSettings);
                  data.updateNodeData(id, { rssSettings: newSettings });
                }}
                className="nodrag"
              />
              
              <Input
                placeholder="Content contains..."
                value={rssSettings.filters.content}
                onChange={(e) => {
                  const newSettings = {
                    ...rssSettings,
                    filters: { ...rssSettings.filters, content: e.target.value }
                  };
                  setRssSettings(newSettings);
                  data.updateNodeData(id, { rssSettings: newSettings });
                }}
                className="nodrag"
              />
  
              <InputNumber
                placeholder="Max items"
                min={1}
                max={50}
                value={rssSettings.maxItems}
                onChange={(value) => {
                  const newSettings = { ...rssSettings, maxItems: value };
                  setRssSettings(newSettings);
                  data.updateNodeData(id, { rssSettings: newSettings });
                }}
                className="nodrag"
                style={{ width: '100%' }}
              />
              
              <Switch
                checkedChildren="Include full content"
                unCheckedChildren="Summary only"
                checked={rssSettings.includeContent}
                onChange={(checked) => {
                  const newSettings = { ...rssSettings, includeContent: checked };
                  setRssSettings(newSettings);
                  data.updateNodeData(id, { rssSettings: newSettings });
                }}
                className="nodrag"
              />
            </Space>
          </div>
  
          <Alert
            type="info"
            message="Available Outputs"
            description={
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                <li>title - Item title</li>
                <li>link - Item URL</li>
                <li>description - Summary text</li>
                <li>content - Full content (if enabled)</li>
                <li>pubDate - Publication date</li>
                <li>author - Author name</li>
                <li>categories - Category list</li>
              </ul>
            }
          />
        </Space>
      )}
  
      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
  
      <Modal
        title="Webhook Trigger Instructions"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleOk}
        width={700}
      >
        {webhookInstructions}
      </Modal>
    </Card>
  );
};

export default TriggerNode;